import React, { useState, useRef } from 'react'
import { useDispatch } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { useTheme, alpha } from '@mui/material'
import Box from '@mui/material/Box'
import Stack from '@mui/material/Stack'
import Button from '@mui/material/Button'
import Alert from '@mui/material/Alert'
import defaultTexts from '../../texts/texts'
import { GoogleReCaptcha, googleReCaptchaValidation } from './GoogleReCaptcha'
import constants from '../../config/constants'
import { getUserTokens } from '../../_network/auth'
import { request } from '../../_network/request'
import { clientAccountsURLs } from '../../_network/apiUrls'
import { setIsAuthenticated, setTokens, setProfile } from '../../store/reducers/profileSlice'
import CustomTextField from './_form_fields/CustomTextField'
import CustomBackdrop from './CustomBackdrop'

const LoginWrapper = (props) => {
  const {
    loginNotification,
    setLoginDialogOpen,
    setSignUpDialogOpen,
    setPasswordResetDialogOpen,
    setValidationEmailResendDialogOpen,
  } = props
  const theme = useTheme()
  const dispatch = useDispatch()
  const navigate = useNavigate()

  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState('')
  const [captchaValidated, setCaptchaValidated] = useState(false)
  const [errorCaptcha, setErrorCaptcha] = useState(false)
  const [isLoading, setIsLoading] = useState(false)

  const captchaRef = useRef('')

  const formControlStyle = {
    background: theme.palette.azureishWhite,
    color: theme.palette.violetsAreBlue,
    borderRadius: '30px',
  }

  const outlinedInputStyle = {
    '& .MuiInputBase-input': {
      color: theme.palette.violetsAreBlue,
    },
    '& .MuiInputBase-input::label': {
      color: theme.palette.violetsAreBlue,
    },
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: 'transparent',
    },
    '&:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: 'transparent',
    },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: 'transparent',
    },
  }

  const forgotPasswordButtonStyle = {
    textTransform: 'capitalize',
    fontWeight: '400',
    color: theme.palette.greenSheen,
    fontSize: '16px',
    textDecoration: 'underline',

    '&:hover': {
      background: 'inherit',
      textDecoration: 'none',
    },
  }

  const signUpButtonStyle = {
    textTransform: 'capitalize',
    fontWeight: '400',
    color: theme.palette.aliceBlue,
    fontSize: '16px',
    textDecoration: 'underline',

    '&:hover': {
      background: 'inherit',
      textDecoration: 'none',
    },
  }

  const loginButtonStyle = {
    fontWeight: '400',
    color: 'white',
    background: theme.palette.violetsAreBlue,
    fontSize: '18px',
    height: '44px',
    borderRadius: '22px',
    padding: '0px 40px',

    '&:hover': {
      background: alpha(theme.palette.violetsAreBlue, 0.8),
    },
  }

  const handleChangeEmail = (e) => {
    setEmail(e.target.value)
  }

  const handleChangePassword = (e) => {
    setPassword(e.target.value)
  }  

  const handleClickSignUp = () => {
    setLoginDialogOpen(false)
    setSignUpDialogOpen(true)
  }

  const handleClickForgotPassword = () => {
    setLoginDialogOpen(false)
    setPasswordResetDialogOpen(true)
  }

  const handleClickValidationEmailResend = () => {
    setLoginDialogOpen(false)
    setValidationEmailResendDialogOpen(true)
  }

  const loginRequest = async () => {
    const authResponse = await getUserTokens({ grantType: 'password', username: email, password })
    
    if (authResponse && authResponse.access_token && authResponse.refresh_token) {
      const profileUrl = constants.core_service_domain + '/' + clientAccountsURLs.getProfile      
      const profileRequest = { url: profileUrl, method: 'GET', accessToken: authResponse.access_token }    
      const profileResponse = await request(profileRequest)
      
      if (profileResponse && profileResponse.status_code && profileResponse.status_code < 400 && profileResponse.data && profileResponse.data.account_status && profileResponse.data.account_status === 'active') {
        let user_role = (profileResponse.data.roles && profileResponse.data.roles.length > 0) ? profileResponse.data.roles[0] : null

        if (profileResponse.data.superadmin) {
          user_role = 'superadmin'
        }

        dispatch(setIsAuthenticated({ isAuthenticated: true }))

        dispatch(setTokens({
          access_token: authResponse.access_token,
          refresh_token: authResponse.refresh_token,
        }))

        dispatch(setProfile({
          account_id: profileResponse.data.account_id,
          account_balance: profileResponse.data.account_balance,
          user_role,
        }))

        setLoginDialogOpen(false)

        navigate('/')
      } else {
        setError(true)
      }
    } else {
      setError(true)
    }
  }

  const handleClickLogin = async () => {
    setIsLoading(true)
    setError(false)

    const captchaResponse = captchaValidated ? true : await googleReCaptchaValidation(captchaRef.current.getValue())
    setCaptchaValidated(captchaResponse ? true : false)

    if (email && password && captchaResponse) {
      loginRequest()
    } else if (!email || !password) {
      setError(true)      
    } else {
      setErrorCaptcha(true)
    }

    setIsLoading(false)
  }

  return (
    <>
      { isLoading && <CustomBackdrop open={isLoading} /> }

      {
        loginNotification &&
        <Box sx={{ padding: '12px 0px' }}>
         <Alert severity='success'>{loginNotification}</Alert>
        </Box>
      }

      {
        error &&
        <Box sx={{ padding: '12px 0px' }}>
         <Alert severity='error'>{defaultTexts.authorizationFailedText}</Alert>
        </Box>
      }

      <Box sx={{ padding: '12px 0px' }}>
        <CustomTextField 
          variant='filled'
          label={defaultTexts.email}
          currentValue={email}
          setCurrentValue={handleChangeEmail}
          sx={formControlStyle}
          outlinedInputSx={outlinedInputStyle}
        />
      </Box>

      <Box sx={{ padding: '12px 0px' }}>
        <CustomTextField 
          type='password'
          variant='filled'
          label={defaultTexts.password}
          currentValue={password}
          setCurrentValue={handleChangePassword}
          sx={formControlStyle}
          outlinedInputSx={outlinedInputStyle}
        />
      </Box>

      <Box sx={{ padding: '12px 0px 32px 0px' }}>
        <center>
          <GoogleReCaptcha
            ref={captchaRef}
            error={errorCaptcha}
            errorColor={theme.palette.congoPink}
            errorCenter={true}
          />
        </center>
      </Box>

      <Stack
        direction={{ xs: 'column', md: 'row' }}
        alignItems={{ xs: 'center', md: 'flex-end' }}
        justifyContent={{ xs: 'flex-start', md: 'space-between' }}
        spacing={1}
      >
        <Box>
          <Stack
            direction='column'
            alignItems={{ xs: 'center', md: 'flex-start' }}
            justifyContent='flex-start'
          >

            <Button
              variant='text'
              disableRipple
              disableElevation
              sx={signUpButtonStyle}
              onClick={handleClickSignUp}
            >
              {defaultTexts.signUp}
            </Button>

            <Button
              variant='text'
              disableRipple
              disableElevation
              sx={forgotPasswordButtonStyle}
              onClick={handleClickForgotPassword}
            >
              {defaultTexts.forgotPassword}
            </Button>

            <Button
              variant='text'
              disableRipple
              disableElevation
              sx={forgotPasswordButtonStyle}
              onClick={handleClickValidationEmailResend}
            >
              {defaultTexts.resendValidationEmail}
            </Button>

          </Stack>
        </Box>
        <Box>
          <Button
            variant='text'
            disableRipple
            disableElevation
            sx={loginButtonStyle}
            onClick={handleClickLogin}
          >
            {defaultTexts.login}
          </Button>
        </Box>
      </Stack>
    </>
  )
}

export default LoginWrapper